import { Node, mergeAttributes } from '@tiptap/core';

/* Docs v1 — Callout: a highlighted aside for "watch out" / "decided" notes.
   Plain content node (no node view needed) — it holds normal blocks, so lists
   and paragraphs nest inside it and all the usual commands keep working. */

const Callout = Node.create({
	name: 'callout',
	group: 'block',
	content: 'block+',
	defining: true,

	addAttributes() {
		return {
			variant: {
				default: 'info',
				parseHTML: (el) => el.getAttribute('data-variant') || 'info',
				renderHTML: (attrs) => ({ 'data-variant': attrs.variant }),
			},
		};
	},

	parseHTML() {
		return [{ tag: 'div[data-callout]' }];
	},

	renderHTML({ HTMLAttributes }) {
		return [
			'div',
			mergeAttributes(HTMLAttributes, { 'data-callout': '', class: 'cyv-callout' }),
			0,
		];
	},

	addCommands() {
		return {
			setCallout:
				(attrs = {}) =>
				({ commands }) =>
					commands.wrapIn(this.name, attrs),
			toggleCallout:
				(attrs = {}) =>
				({ commands }) =>
					commands.toggleWrap(this.name, attrs),
			unsetCallout:
				() =>
				({ commands }) =>
					commands.lift(this.name),
		};
	},

	addKeyboardShortcuts() {
		return {
			/* Shift-Enter exits the callout into a fresh paragraph beneath it.
			   StarterKit's code block already exits on Shift-Enter, so a callout
			   that instead inserted a line break was inconsistent with the only
			   other "container" block in the editor. Returns false when the cursor
			   isn't in a callout, so the normal hard-break behaviour is untouched
			   everywhere else. */
			'Shift-Enter': ({ editor }) => {
				const { $from } = editor.state.selection;
				for (let d = $from.depth; d > 0; d -= 1) {
					if ($from.node(d).type.name === this.name) {
						const after = $from.after(d);
						return editor
							.chain()
							.insertContentAt(after, { type: 'paragraph' })
							.setTextSelection(after + 1)
							.focus()
							.run();
					}
				}
				return false;
			},

			// Backspace at the very start of an empty callout lifts out of it,
			// so it never becomes a block you can't escape.
			Backspace: ({ editor }) => {
				const { empty, $anchor } = editor.state.selection;
				if (!empty || $anchor.parent.type.name !== 'paragraph') return false;
				if ($anchor.parentOffset !== 0) return false;
				const parent = $anchor.node(-1);
				if (parent?.type.name !== this.name) return false;
				if (parent.childCount > 1) return false;
				return editor.commands.lift(this.name);
			},
		};
	},
});

export default Callout;
